import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gamePlayService } from '../../services/db';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../constants/routes';
import Spinner from '../../components/ui/Spinner';
import {
  GlobeLanguageIcon,
  ClockIcon,
  HelpCircleIcon,
  CalendarIcon,
  TargetIcon,
  ShieldIcon,
  WarningIcon,
  UserIcon,
  LinkIcon,
} from '../../components/common/Icons';



const formatTimestamp = (val) => {
  if (!val) return '—';
  try {
    let date;
    if (val.toDate && typeof val.toDate === 'function') {
      date = val.toDate();
    } else if (val.seconds) {
      date = new Date(val.seconds * 1000);
    } else if (val instanceof Date) {
      date = val;
    } else {
      date = new Date(val);
    }
    if (isNaN(date.getTime())) return '—';

    const pad = (n) => String(n).padStart(2, '0');
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  } catch {
    return '—';
  }
};

const QuizLobbyPage = () => {
  const { seasonId, quizId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      navigate(ROUTES.PUBLIC.HOME, { replace: true });
    }
  }, [user, navigate]);

  const [quizInfo, setQuizInfo] = useState(null);
  const [seasonDetails, setSeasonDetails] = useState(null);
  const [userGameCheck, setUserGameCheck] = useState(false);
  const [leaderboardViewData, setLeaderboardViewData] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('approved');
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      setLoading(true);
      try {
        const [quiz, season, lbSetting] = await Promise.all([
          gamePlayService.getQuizDetails(seasonId, quizId),
          gamePlayService.getSeasonDetails(seasonId),
          gamePlayService.getSeasonLeaderboardSetting(seasonId),
        ]);

        if (isMounted) {
          setQuizInfo(quiz);
          setSeasonDetails(season);
          if (lbSetting && lbSetting.appearance) {
            setLeaderboardViewData(lbSetting.appearance);
          }
        }

        // Check user participation status
        if (user?.uid) {
          const res = await gamePlayService.getUserResult(seasonId, quizId, user.uid);
          if (isMounted) {
            setUserGameCheck(!!res);
          }

          // Check season permission if private
          if (season?.visibility?.toLowerCase() === 'private') {
            const perm = await gamePlayService.getSeasonPermissionStatus(seasonId, user.uid);
            if (isMounted) setPermissionStatus(perm);
          }
        } else {
          // Check local storage for guest
          try {
            const stored = JSON.parse(localStorage.getItem('gamesPlayed') || '[]');
            if (stored.includes(quizId)) {
              if (isMounted) setUserGameCheck(true);
            }
          } catch {}
        }
      } catch (err) {
        console.error('Failed to load quiz intro:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (seasonId && quizId) {
      loadData();
    }

    return () => {
      isMounted = false;
    };
  }, [seasonId, quizId, user]);

  const handleRequestAccess = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/play/season/${seasonId}/quiz/${quizId}` } });
      return;
    }
    setRequesting(true);
    try {
      await gamePlayService.requestSeasonAccess(seasonId, user);
      setPermissionStatus('pending');
    } catch (err) {
      console.error('Failed to request access:', err);
    } finally {
      setRequesting(false);
    }
  };

  const handleShare = () => {
    const text = `📖 Join me in testing our Bible knowledge! Try the quiz "${quizInfo?.title || ''}" here: https://go.biblecontest.app/quiz/${seasonId}/${quizId}`;
    if (navigator.share) {
      navigator.share({ title: quizInfo?.title || 'Bible Contest', text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      alert('Quiz link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: '#FBF6EF', minHeight: '90vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (!quizInfo) {
    return (
      <div style={{ backgroundColor: '#FBF6EF', minHeight: '90vh', padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(217, 119, 6, 0.1)', color: '#D97706', marginBottom: '16px' }}>
          <WarningIcon size={32} />
        </div>
        <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#332E2A' }}>Quiz Not Found</h2>

        <p style={{ color: 'rgba(51, 46, 42, 0.7)', marginBottom: '20px' }}>
          Unable to load quiz details. Please contact your host.
        </p>
        <button
          type="button"
          onClick={() => navigate(`/play/season/${seasonId}`)}
          style={{
            backgroundColor: '#FF5C56',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '100px',
            padding: '12px 24px',
            fontWeight: 'bold',
            cursor: 'pointer',
          }}
        >
          ← Back to Season
        </button>
      </div>
    );
  }

  const isFinished = (quizInfo.status || '').toLowerCase() === 'finished';
  const isOngoing = (quizInfo.status || '').toLowerCase() === 'ongoing';
  const isUpcoming = (quizInfo.status || '').toLowerCase() === 'upcoming';
  const questionCount = quizInfo?.number_of_questions ?? quizInfo?.numberOfQuestions ?? quizInfo?.questions_count ?? (quizInfo?.questions ? quizInfo.questions.length : 0);
  const hasNoQuestions = questionCount === 0;

  // Private Fellowship Permission Screen (matching QuizIntro.dart permission check)
  if (permissionStatus !== 'approved' && !isFinished) {
    const isPending = permissionStatus === 'pending';
    const isRejected = permissionStatus === 'rejected';

    return (
      <div style={{ backgroundColor: '#FBF6EF', minHeight: '90vh', padding: '24px 16px', display: 'flex', flexDirection: 'column' }}>
        <button
          type="button"
          className="page-header__back"
          onClick={() => navigate(`/play/season/${seasonId}`)}
          aria-label="Back to season"
          style={{ marginBottom: '24px' }}
        >
          ←
        </button>

        <div style={{ maxWidth: '480px', margin: 'auto', textAlign: 'center' }}>
          <div
            style={{
              width: '88px',
              height: '88px',
              borderRadius: '50%',
              backgroundColor: isPending ? 'rgba(192, 133, 82, 0.15)' : 'rgba(255, 92, 86, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px',
              margin: '0 auto 24px',
            }}
          >
            {isPending ? (
              <ClockIcon size={40} style={{ color: '#C08552' }} />
            ) : isRejected ? (
              <ShieldIcon size={40} style={{ color: '#DC2626' }} />
            ) : (
              <ShieldIcon size={40} style={{ color: '#FF5C56' }} />
            )}
          </div>


          <h2 style={{ fontSize: '26px', fontWeight: 'bold', color: '#332E2A', margin: '0 0 12px' }}>
            {isPending ? 'Request Under Review' : isRejected ? 'Access Not Approved' : 'Private Fellowship Season'}
          </h2>
          <p style={{ fontSize: '15px', color: 'rgba(51, 46, 42, 0.75)', lineHeight: 1.6, margin: '0 0 32px' }}>
            {isPending
              ? 'Your request has been sent to the host. You will receive access as soon as it is approved.'
              : isRejected
              ? 'Your previous request was not approved by the host. You may submit a new request below.'
              : 'This season is a private fellowship and requires permission from the host to join. Send your request below to participate!'}
          </p>

          {!isPending && (
            <button
              type="button"
              disabled={requesting}
              onClick={handleRequestAccess}
              style={{
                width: '100%',
                padding: '14px 24px',
                borderRadius: '12px',
                backgroundColor: '#FF5C56',
                color: '#FFFFFF',
                fontWeight: 'bold',
                fontSize: '15px',
                border: 'none',
                cursor: requesting ? 'wait' : 'pointer',
              }}
            >
              {requesting ? 'Submitting...' : isRejected ? 'Submit New Request' : 'Request to Join'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#FBF6EF', minHeight: '90vh', padding: '16px 12px 32px' }}>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        {/* Top App Bar with back & share buttons matching PageHeader */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <button
            type="button"
            className="page-header__back"
            onClick={() => navigate(`/play/season/${seasonId}`)}
            aria-label="Back to season"
          >
            ←
          </button>

          <button
            type="button"
            className="page-header__back"
            onClick={handleShare}
            title="Share Quiz"
            aria-label="Share Quiz"
          >
            <LinkIcon size={16} />
          </button>
        </div>

        {/* BebasNeue / Bold Title Header */}
        <div style={{ marginBottom: '16px' }}>
          <h1
            style={{
              fontSize: 'clamp(28px, 6vw, 38px)',
              fontWeight: 'bold',
              color: '#332E2A',
              margin: '0',
              lineHeight: 1.15,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            {quizInfo.title}
          </h1>
        </div>

        {/* Info Sheet Card matching Flutter QuizIntro */}
        <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid rgba(51, 46, 42, 0.08)',
            padding: '16px 20px',
            marginBottom: '20px',
          }}
        >
          {seasonDetails && (
            <>
              <InfoRow icon={<TargetIcon size={16} style={{ color: 'var(--clr-primary)' }} />} label="Season" value={seasonDetails.season} />
              <div style={{ height: '1px', backgroundColor: '#F5EFE6', margin: '12px 0' }} />
              <InfoRow icon={<UserIcon size={16} style={{ color: 'var(--clr-muted)' }} />} label="Host" value={seasonDetails.host} />
              <div style={{ height: '1px', backgroundColor: '#F5EFE6', margin: '12px 0' }} />
              {seasonDetails.languages && (
                <>
                  <InfoRow
                    icon={<GlobeLanguageIcon size={16} style={{ color: '#2563EB' }} />}
                    label="Language"
                    value={
                      Array.isArray(seasonDetails.languages)
                        ? seasonDetails.languages.join(', ')
                        : seasonDetails.languages
                    }
                  />
                  <div style={{ height: '1px', backgroundColor: '#F5EFE6', margin: '12px 0' }} />
                </>
              )}
            </>
          )}

          <InfoRow icon={<ClockIcon size={16} style={{ color: '#FF5C56' }} />} label="Duration" value={`${quizInfo.duration || 5} mins`} />
          <div style={{ height: '1px', backgroundColor: '#F5EFE6', margin: '12px 0' }} />

          <InfoRow
            icon={<HelpCircleIcon size={16} style={{ color: '#64748B' }} />}
            label="Questions"
            value={`${quizInfo.number_of_questions ?? quizInfo.numberOfQuestions ?? quizInfo.questions_count ?? (quizInfo.questions ? quizInfo.questions.length : 0)}`}
          />
          <div style={{ height: '1px', backgroundColor: '#F5EFE6', margin: '12px 0' }} />

          <InfoRow
            icon={<CalendarIcon size={16} style={{ color: '#D97706' }} />}
            label="Starts At"
            value={formatTimestamp(quizInfo.start || quizInfo.startDate)}
          />
          <div style={{ height: '1px', backgroundColor: '#F5EFE6', margin: '12px 0' }} />

          <InfoRow
            icon={<TargetIcon size={16} style={{ color: '#059669' }} />}
            label="Ends At"
            value={formatTimestamp(quizInfo.end || quizInfo.endDate)}
          />
        </div>

        {/* About Quiz Subtitle & Description (Scripture portion) */}
        {quizInfo.description && quizInfo.description.trim().length > 0 && (
          <div style={{ marginBottom: '28px' }}>
            <div style={{ marginBottom: '8px' }}>
              <div style={{ color: '#332E2A', fontSize: '16px', fontWeight: 'bold' }}>
                About Quiz
              </div>
              <div
                style={{
                  width: '40px',
                  height: '3px',
                  backgroundColor: '#FF5C56',
                  borderRadius: '1.5px',
                  marginTop: '4px',
                }}
              />
            </div>

            <div
              style={{
                backgroundColor: 'rgba(192, 133, 82, 0.05)',
                borderRadius: '12px',
                border: '1px solid rgba(192, 133, 82, 0.15)',
                padding: '16px',
                color: 'rgba(51, 46, 42, 0.85)',
                fontSize: '14px',
                lineHeight: 1.6,
              }}
            >
              {quizInfo.description}
            </div>
          </div>
        )}

        {/* Action Buttons Section matching getStatus() in Flutter QuizIntro */}
        <div style={{ marginTop: '32px' }}>
          {isOngoing && userGameCheck && leaderboardViewData === 'after_quiz_finished' ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: '20px', color: '#332E2A', fontWeight: 'bold', marginBottom: '8px' }}>
                Your answers are submitted!
              </div>
              <div style={{ fontSize: '14px', color: '#332E2A' }}>
                Rest in God's peace while scores & standings are finalized.
              </div>
            </div>
          ) : isOngoing && userGameCheck ? (
            <ActionButton
              title="View Leaderboard"
              subtitle="See how you rank with fellow seekers"
              primary
              onClick={() => navigate(`/play/season/${seasonId}/quiz/${quizId}/leaderboard`)}
            />
          ) : isOngoing && hasNoQuestions ? (
            <div
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                borderRadius: '16px',
                padding: '24px 20px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '18px', color: '#DC2626', fontWeight: 700, marginBottom: '8px' }}>
                No Questions in Quiz
              </div>
              <div style={{ fontSize: '14.5px', color: '#991B1B', lineHeight: 1.5 }}>
                There are no questions in the quiz, please reachout to season host.
              </div>
            </div>
          ) : isOngoing ? (
            <ActionButton
              title="Start Quiz"
              subtitle="May God's Word guide & inspire you!"
              primary
              onClick={() => {
                if (hasNoQuestions) {
                  alert('There are no questions in the quiz, please reachout to season host.');
                  return;
                }
                if (!user) {
                  navigate('/login', {
                    state: { from: `/play/season/${seasonId}/quiz/${quizId}/play` },
                  });
                } else {
                  navigate(`/play/season/${seasonId}/quiz/${quizId}/play`);
                }
              }}
            />

          ) : isUpcoming ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: '24px', color: '#332E2A', fontWeight: 'bold', marginBottom: '8px' }}>
                Opening Soon!
              </div>
              <div style={{ fontSize: '14px', color: '#332E2A' }}>
                Read, reflect, and stay ready in the Word. 🙏
              </div>
            </div>
          ) : isFinished ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <ActionButton
                title="Answer Key & Scripture"
                subtitle=""
                secondary
                onClick={() => navigate(`/play/season/${seasonId}/quiz/${quizId}/answers`)}
              />
              <ActionButton
                title="View Final Standings"
                subtitle="Celebrate everyone's efforts"
                primary
                onClick={() => navigate(`/play/season/${seasonId}/quiz/${quizId}/leaderboard`)}
              />
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#332E2A' }}>
              Unable to load quiz status. Please contact your host.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
      <span style={{ color: 'rgba(51, 46, 42, 0.55)', fontSize: '14px', fontWeight: 500 }}>
        {label}
      </span>
    </div>
    <span style={{ color: '#332E2A', fontSize: '14px', fontWeight: 'bold' }}>{value}</span>
  </div>
);


const ActionButton = ({ title, subtitle, primary, secondary, onClick }) => (
  <div style={{ textAlign: 'center' }}>
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        padding: '16px 20px',
        borderRadius: '16px',
        border: secondary ? '1.5px solid #E2DCD5' : 'none',
        backgroundColor: primary ? '#FF5C56' : '#FFFFFF',
        color: primary ? '#FFFFFF' : '#332E2A',
        fontWeight: 'bold',
        fontSize: '16px',
        cursor: 'pointer',
        boxShadow: primary ? '0 4px 14px rgba(255, 92, 86, 0.25)' : 'none',
        transition: 'transform 0.15s ease',
      }}
    >
      {title}
    </button>
    {subtitle && (
      <div style={{ fontSize: '12px', color: 'rgba(51, 46, 42, 0.65)', marginTop: '6px' }}>
        {subtitle}
      </div>
    )}
  </div>
);

export default QuizLobbyPage;
